import { useCallback, useMemo, useRef } from 'react';
import type { StatsigOptions, StatsigUser } from '@statsig/react-bindings';
import type { StatsigOptions as LegacyStatsigOptions } from 'statsig-react';
import { useIsInAppBrowser } from '~/common/useIsInAppBrowser';
import { isNewStatsigSDK } from '~/common/Statsig/hybridStatsig';
import { LoggerService } from '~/services/logger/logger.service';
import {
    getBuyNowExperienceData,
    getDataFromMarketingSite
} from '~/components/Signup/utils/signup-form.utils';
import { MARKETING_STABLE_ID_COOKIE_NAME } from '~/components/Signup/constants/general';
import { getCookie } from '~/utils/dom.utils';
import type { User } from '~/models/user.model';
import type { Company } from '~/models/company.model';
import type { ConfigStore } from '~/stores/config.store';
import type { INetworkStore } from '~/types/dependency.types';
import type { StatsigUserCustom } from '~/common/Statsig/types';
import { isReactFirst } from '~/utils/environment.utils';

export interface UseStatsigOptionsProps {
    configStore: ConfigStore;
    networkStore: INetworkStore;
    currUser?: User;
    currCompany?: Company;
    statsigUserCustomOverride?: StatsigUserCustom;
    isUnauthenticatedContext?: boolean;
    mountKey?: string;
}

const stableIdFromMarketing = getCookie(MARKETING_STABLE_ID_COOKIE_NAME);

export const useStatsigOptions = ({
    currUser,
    configStore,
    networkStore,
    currCompany,
    statsigUserCustomOverride,
    isUnauthenticatedContext,
    mountKey
}: UseStatsigOptionsProps) => {
    const isTestEnv = configStore.isTest();
    const isInAppBrowser = useIsInAppBrowser();

    const hasUser = useCallback(() => !!currUser?._id, [currUser]);
    const hasUserAndCompany = useCallback(
        () => !!currUser?._id && !!currCompany?._id,
        [currUser, currCompany]
    );

    /**
     * Stop the SDK from issuing any network requests and make it only operate with
     * local overrides, then cache, then defaults (false for gates and defaults provided in code for experiments).
     */
    const isToForceLocalMode = useRef(false);

    const isBuyNowExperience =
        getBuyNowExperienceData().isEmbeddedBuyNowExperience;

    const logger = useMemo(
        () =>
            new LoggerService({
                messagePrefix: '[Statsig]',
                commonContext: {
                    statsig_service_type: 'React',
                    user_id: currUser?._id,
                    company_id: currCompany?._id,
                    account_id: currUser?.account_id,
                    network_status: {
                        navigatorOnLine: networkStore.isOnline
                    },
                    is_statsig_new_sdk: isNewStatsigSDK(),
                    is_react_first: isReactFirst()
                }
            }),
        [
            currCompany?._id,
            currUser?._id,
            currUser?.account_id,
            networkStore.isOnline
        ]
    );

    const reportError = useCallback(
        (
            error: Error,
            message: string,
            extraContext: { [key: string]: any } = {}
        ) => {
            extraContext.statsig_error = message;
            logger.trackError({ error, message, context: extraContext });
        },
        [logger]
    );

    const handleSuccess = useCallback(
        (initDurationMs: number) => {
            const randomNum = Math.floor(Math.random() * 101);
            const percentage =
                configStore.config.statsig_log_init_timeout_percentage || 0;
            if (randomNum <= percentage) {
                logger.logInfo('initCompletionCallback success', {
                    statsig_init_duration_ms: initDurationMs
                });
            }
        },
        [configStore.config.statsig_log_init_timeout_percentage, logger]
    );

    const handleError = useCallback(
        (error: Error, message?: string) => {
            reportError(
                new Error(error?.message || 'unknown error'),
                message || 'Statsig error'
            );
        },
        [reportError]
    );

    const getStatsigOptions = useCallback(() => {
        try {
            const baseOptions = {
                environment: {
                    tier: configStore.isE2E()
                        ? configStore.statsigE2ETier
                        : configStore.config.environment
                }
            };

            if (isNewStatsigSDK()) {
                const statsigOptions: StatsigOptions = {
                    ...baseOptions,
                    networkConfig: {
                        preventAllNetworkTraffic:
                            !!isTestEnv || isToForceLocalMode.current,
                        api:
                            configStore.statsigApiUrl ||
                            'https://statsigapi.net/v1'
                    }
                };
                return statsigOptions;
            } else {
                const legacyStatsigOptions: LegacyStatsigOptions = {
                    ...baseOptions,
                    localMode: !!isTestEnv || isToForceLocalMode.current,
                    api:
                        configStore.statsigApiUrl ||
                        'https://statsigapi.net/v1',
                    initCompletionCallback: (
                        initDurationMs: number,
                        success: boolean,
                        message: string | null
                    ) => {
                        if (success) {
                            handleSuccess(initDurationMs);
                        } else {
                            handleError(
                                new Error(message ? message : 'unknown error'),
                                'initCompletionCallback error'
                            );
                        }
                    },
                    gateEvaluationCallback: (
                        key: string,
                        value: boolean
                    ): void => {
                        window.DD_RUM?.addFeatureFlagEvaluation?.(key, value);
                    },
                    updateUserCompletionCallback: (
                        durationMs: number,
                        success: boolean,
                        message: string | null
                    ) => {
                        if (!success) {
                            handleError(
                                new Error(message ? message : 'unknown error'),
                                'updateUserCompletionCallback error'
                            );
                        }
                    },
                    initTimeoutMs:
                        configStore.config.statsig_initialization_timeout ||
                        3000
                };

                // Handle stableID for legacy package
                const stableIDFromUser =
                    currUser?.statsig_stable_id || stableIdFromMarketing;
                if (stableIDFromUser) {
                    legacyStatsigOptions.overrideStableID = stableIDFromUser;
                }

                return legacyStatsigOptions;
            }
        } catch (err) {
            reportError(err, 'statsig- error building statsigOptions');

            return isNewStatsigSDK()
                ? { networkConfig: { preventAllNetworkTraffic: true } }
                : { localMode: true };
        }
    }, [
        configStore,
        currUser?.statsig_stable_id,
        isTestEnv,
        handleError,
        handleSuccess,
        reportError
    ]);

    const hasFlows = currUser?.flowsBetaUserType?.hasFlows;
    const trialStartDate = currUser?.getTrialStartDate();
    const isSubscribed = !!currUser?.companyHasSubscription;
    const isActivated = currUser?.isActivated();
    const isActive = currUser?.isActive();

    const getUserCustomAttributes = useCallback((): StatsigUser['custom'] => {
        const financeAccountStatus = currUser.account?.finance_account_status;

        return {
            intent: currUser.user_intent,
            trial_start_date: trialStartDate,
            intent_infra_variation: currUser.ab_tests.intent_infra,
            growth_2024_variation: currUser.ab_tests.growth_2024,
            company_id: currUser.company_id,
            company_vertical: currCompany?.company_vertical,
            company_type:
                currCompany?.company_type === 'other'
                    ? currCompany?.other_company_type
                    : currCompany?.company_type,
            is_demo: currUser.hb_user_type?.indexOf('demo') >= 0,
            is_multi_brand: currUser?.hasMultipleCompanies(),
            has_bank_account: !!currCompany?.bank_account_payment_method_id,
            has_debit_card: !!currUser?.myDebitCard,
            instant_payout_allowed: !!currCompany?.isEligibleForInstantPayout,
            company_country: currCompany?.country?.toUpperCase(),
            is_activated: isActivated,
            is_active: isActive,
            is_nev: currCompany?.isNev,
            is_subscribed: isSubscribed,
            otam_group: currUser?.account?.otam_group,
            account_id: currUser?.account_id,
            has_smart_files: hasFlows,
            has_legacy_files: !currUser?.isBlockFilesFlagEnabled,
            is_account_owner: currUser?.is_account_owner,
            is_pro: currUser?.isPro,
            utm_source:
                currUser?.attribution?.user_utm_params?.utm_source ||
                currCompany?.owner?.attribution?.user_utm_params?.utm_source,
            utm_campaign:
                currUser?.attribution?.user_utm_params?.utm_campaign ||
                currCompany?.owner?.attribution?.user_utm_params?.utm_campaign,
            utm_medium:
                currUser?.attribution?.user_utm_params?.utm_medium ||
                currCompany?.owner?.attribution?.user_utm_params?.utm_medium,
            utm_initiative:
                currUser?.attribution?.user_utm_params?.utm_initiative ||
                currCompany?.owner?.attribution?.user_utm_params
                    ?.utm_initiative,
            utm_partnership:
                currUser?.attribution?.user_utm_params?.partnership ||
                currCompany?.owner?.attribution?.user_utm_params?.partnership,
            promo_code: currUser?.attribution?.user_utm_params?.promo_code,
            initial_referral_code:
                currUser?.attribution?.user_utm_params?.utm_mbsy,
            onboarding_start_device:
                currUser?.onboarding_container?.onboarding_start_device,
            subscription_start_date:
                currUser?.account?.subscription?.start_date,
            owner_onboarding_start_device:
                currCompany?.owner?.onboarding_container
                    ?.onboarding_start_device,
            finance_application_approved_ts:
                financeAccountStatus?.application_approved_ts,
            finance_application_awaiting_documents_ts:
                financeAccountStatus?.application_awaiting_documents_ts,
            finance_account_closed_ts: financeAccountStatus?.account_closed_ts,
            has_gmail_integration: currUser?.activeGoogleIntegrations.gmail,
            is_pre_trial_collaborator: currUser?.isAtwsPreTrialVendor,
            is_trial_from_marketing_site_collaborator:
                currUser?.isAtwsTrialVendor &&
                !!currUser?.attribution?.external_id,
            plan_type: currUser?.account?.subscription?.pricing_plan?.plan_type,
            card_on_file_enabled: !!currUser?.account?.card_on_file_enabled,
            is_educator: !!currUser?.isEducator,
            is_user_start_from_template:
                !!currUser?.attribution?.template_parameters?._id,
            is_powered_by: currCompany?.is_powered_by_hb,
            ...(statsigUserCustomOverride?.custom || {})
        };
    }, [
        currUser,
        trialStartDate,
        currCompany?.company_vertical,
        currCompany?.company_type,
        currCompany?.other_company_type,
        currCompany?.bank_account_payment_method_id,
        currCompany?.isEligibleForInstantPayout,
        currCompany?.country,
        currCompany?.isNev,
        currCompany?.owner?.attribution?.user_utm_params?.utm_source,
        currCompany?.owner?.attribution?.user_utm_params?.utm_campaign,
        currCompany?.owner?.attribution?.user_utm_params?.utm_medium,
        currCompany?.owner?.attribution?.user_utm_params?.utm_initiative,
        currCompany?.owner?.attribution?.user_utm_params?.partnership,
        currCompany?.owner?.onboarding_container?.onboarding_start_device,
        currCompany?.is_powered_by_hb,
        statsigUserCustomOverride?.custom,
        isActivated,
        isActive,
        isSubscribed,
        hasFlows
    ]);

    const getCustomIDs = useCallback((): StatsigUser['customIDs'] => {
        const marketingStableId = stableIdFromMarketing
            ? { stableID: stableIdFromMarketing }
            : {};

        const accountAndCompanyIds = hasUser()
            ? {
                  account_id: currUser.account_id,
                  company_id: currUser.company_id
              }
            : {};

        const userStableId =
            hasUser() && currUser.statsig_stable_id
                ? { stableID: currUser.statsig_stable_id }
                : {};

        return {
            ...marketingStableId,
            ...accountAndCompanyIds,
            ...userStableId,
            ...(statsigUserCustomOverride?.customIDs || {})
        };
    }, [
        hasUser,
        currUser?.account_id,
        currUser?.company_id,
        currUser?.statsig_stable_id,
        statsigUserCustomOverride?.customIDs
    ]);

    const getCustomAttributes = useCallback((): StatsigUser['custom'] => {
        let customAttributes: StatsigUser['custom'] = {};
        const marketingSiteData = getDataFromMarketingSite();

        try {
            if (hasUserAndCompany()) {
                customAttributes = getUserCustomAttributes();
            }

            if (configStore.isDevelopment()) {
                customAttributes.local_user = configStore.config.local_user;
            } else if (configStore.isStaging()) {
                customAttributes.staging_env_name =
                    configStore.config.staging_env_name;
            }

            customAttributes.is_one_tap =
                marketingSiteData?.userData?.sso_type === 'google_one_tap';
            customAttributes.is_in_app_browser = isInAppBrowser;
            customAttributes.signup_landing_page =
                marketingSiteData.signup_landing_page;
            customAttributes.signup_is_buy_now_experience = isBuyNowExperience;
            customAttributes.requestUrl = window.location.href;
            customAttributes.isWebflowVisitor =
                document.referrer?.includes('/lp/');
            customAttributes.is_powered_by =
                window?.clientPortalConfiguration?.is_powered_by_hb;
        } catch (err) {
            reportError(err, 'statsig- error building custom attributes');
            isToForceLocalMode.current = true;
        }

        return customAttributes;
    }, [
        configStore,
        getUserCustomAttributes,
        hasUserAndCompany,
        reportError,
        isInAppBrowser,
        isBuyNowExperience
    ]);

    const buildStatsigUser = useCallback((): StatsigUser | null => {
        if (isUnauthenticatedContext) {
            return null;
        }

        const statsigUser: StatsigUser = { userID: undefined };
        try {
            if (hasUser()) {
                statsigUser.userID = currUser._id;
                statsigUser.email = currUser.email;
            }

            statsigUser.custom = getCustomAttributes();
            statsigUser.customIDs = {
                ...statsigUser.customIDs,
                ...getCustomIDs()
            };
        } catch (err) {
            reportError(err, 'statsig- error building statsigUser', {
                statsigUser
            });
            isToForceLocalMode.current = true;
        }

        return statsigUser;
    }, [
        currUser?._id,
        currUser?.email,
        getCustomAttributes,
        getCustomIDs,
        hasUser,
        isUnauthenticatedContext,
        reportError
    ]);

    const clientParams = useMemo(() => {
        const user = buildStatsigUser();
        const options = getStatsigOptions();
        const sdkKey = configStore.config.statsig_client_api_key;

        return { user, options, sdkKey };
    }, [
        buildStatsigUser,
        getStatsigOptions,
        configStore.config.statsig_client_api_key
    ]);

    const statsigProviderProps = useMemo(
        () => ({ ...clientParams, waitForInitialization: true, mountKey }),
        [clientParams, mountKey]
    );

    return {
        ...clientParams,
        statsigProviderProps,
        mountKey,
        handleSuccess,
        handleError
    };
};
